import React, { useEffect, useState } from 'react'
import { cloneDeep } from 'lodash'

// Modals
import MainModal from '../../modals/common/MainModal'
import { ModalBackButton } from '../../modals/components/ModalBackButton'
import { IconError } from '../../icons/IconError'
import { InputUploadMemberPictureRow } from '../../inputs/InputUploadMemberPictureRow'
import { InputNewMemberRow } from '../../inputs/InputNewMemberRow'
import { InputCheckMemberInfo } from '../../inputs/InputCheckMemberInfo'

// Helpers
import { isValidEmail } from '../../../helpers/isValidEmail'
import useToasterHelper from '../../../helpers/ToasterHelper'
import { authHelpers } from '../../../helpers/helpers'
import { uploadPicture } from '../../../helpers/uploadPicture'

// Hooks
import useUserAccountHook from '../../../hooks/users/competitor/profile/useAccountHook'

// Store
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  riderProfileRegistrationActions,
  selectRiderProfileNewTeamRider,
  selectRiderProfileRider,
  selectRiderProfileTeam,
  selectRiderProfileType,
} from '../../../store/riderProfileRegistration/riderProfileRegistrationSlice'

// Types
import { IInputUserData, IInputUserKey } from '../types/inputData'
import { ECompetitorTypes } from '../../../types/competitor_types'

import { initialUserInputs, inputUserIcons } from './data/input-data'

// Const
import { MESSAGES_CONST } from '../../../const/messages-const'
import { MODAL_CONSTS } from '../../../const/modal-const'
import { CONST } from '../../../const/const'

type Props = {
  show: boolean
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn: {}
}

const userIcon = '/assets/img/User2.png'
const USER_PATH = CONST.DATA.STORAGE.USERS.COMPETITOR_PROFILE_IMAGE_URL.PREFIX

export const AddNewTeamMemberModal = (props: Props) => {
  const { checkValidity, onChangeHandler } = useUserAccountHook()
  const toastFunctions = useToasterHelper()
  const dispatch = useAppDispatch()

  const [inputData, setInputDate] = useState<IInputUserData>(cloneDeep(initialUserInputs))
  const [picture, setPicture] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isValid, setIsValid] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [imgLoading, setImgLoading] = useState(false)
  const [loading, setLoading] = useState(false)

  const riderProfileType = useAppSelector(selectRiderProfileType)
  const riderProfileNewTeamRider = useAppSelector(selectRiderProfileNewTeamRider)
  const riderProfileTeam = useAppSelector(selectRiderProfileTeam)
  const riderProfileRider = useAppSelector(selectRiderProfileRider)

  useEffect(() => {
    if (riderProfileNewTeamRider) {
      setInputDate(riderProfileNewTeamRider.inputData)
      setIsConfirmed(true)

      if (riderProfileNewTeamRider?.picture) {
        const imageUrl = URL.createObjectURL(riderProfileNewTeamRider?.picture)
        setPicture(imageUrl)
      }
    }
  }, [riderProfileNewTeamRider])

  useEffect(() => {
    setIsValid(isConfirmed && checkValidity(inputData))
  }, [inputData, isConfirmed])

  const onSubmit = async () => {
    setLoading(true)
    try {
      if (!isValidEmail(`${inputData?.email?.value}`)) {
        const newInputData = cloneDeep(inputData)
        newInputData['email'].hasError = true
        setInputDate(newInputData)
        toastFunctions.error({ message: 'Invalid email address' })
        return null
      }

      if (!inputData.userFullName?.value?.split(' ')[1]) {
        toastFunctions.error({
          message: 'The full name should start with the first name, followed by the last name.',
        })
        return null
      }

      const responseError = await authHelpers.isUsernameUnique(inputData?.userName?.value)
      if (responseError) {
        toastFunctions.error({ message: responseError })
        return null
      }

      dispatch(
        riderProfileRegistrationActions.setNewTeamRider({
          picture: file,
          inputData,
        })
      )

      props.handleModal(false, MODAL_CONSTS.ADD_NEW_TEAM_MEMBER)

      if (riderProfileType === ECompetitorTypes.teamRider) {
        props.handleModal(true, MODAL_CONSTS.CONFIRM_PROFILE_DETAILS)
      } else {
        props.handleModal(true, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER, {
          backHandler: () => {
            props.handleModal?.(false, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER)
            props.handleModal?.(true, MODAL_CONSTS.ADD_NEW_TEAM_MEMBER)
          },
        })
      }
    } catch (error) {
      console.log(error, 'error')
    } finally {
      setLoading(false)
    }
  }

  const updateProfilePic = async (event: any) => {
    setImgLoading(true)

    const files = event.target.files
    const currentFile = files[0]
    if (!currentFile) return toastFunctions.info({ message: MESSAGES_CONST.NO_FILE_SELECTED })
    try {
      if (riderProfileRider) {
        setFile(currentFile)
        const imageUrl = URL.createObjectURL(currentFile)
        setPicture(imageUrl)
      } else {
        const downloadUrl = await uploadPicture(currentFile, USER_PATH)
        if (downloadUrl) setPicture(downloadUrl)
        toastFunctions.success({ message: MESSAGES_CONST.PIC_UPDATED })
      }
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setImgLoading(false)
    }
  }

  const backHandler = () => {
    props.handleModal(false, MODAL_CONSTS.ADD_NEW_TEAM_MEMBER)
    props.handleModal(true, MODAL_CONSTS.REGISTER_TEAM, {
      userTeamMembers: riderProfileTeam,
      competitor: riderProfileRider,
    })
  }

  return (
    <>
      <MainModal
        title="Add new team member"
        description={'Create account for a team member and invite them to Pegasus'}
        show={props.show}
        type="ADD_NEW_TEAM_MEMBER"
        size="md"
        goBackBtn={
          riderProfileType === ECompetitorTypes.teamRider && (
            <ModalBackButton onClick={backHandler} />
          )
        }
        onTransitionEnd={() => null}
        titleClassName="!font-normal"
        buttons={[
          {
            loading,
            label: 'NEXT >',
            bgClass: 'bg-SeabiscuitMainThemeColor',
            onClick: onSubmit,
            className: 'outline-none !w-full',
            textClass: 'text-white',
            disabled: !isValid,
          },
          {
            label: 'CANCEL',
            bgClass: 'bg-SeabiscuitLightThemeColor',
            className: 'outline-none !w-full',
            borderClass: 'border border-transparent',
            textClass: 'text-SeabiscuitLightTextColor',
            onClick: () => {
              props.handleModal(false, MODAL_CONSTS.ADD_NEW_TEAM_MEMBER)
              dispatch(riderProfileRegistrationActions.clear())
            },
          },
        ]}
      >
        <div className={'flex flex-col gap-4 mt-4'}>
          <div className={'flex flex-col gap-2 border-b border-[#D3DAEE] pb-4'}>
            <InputUploadMemberPictureRow
              title={'Profile picture'}
              isLoading={imgLoading}
              picture={picture}
              onchangeHandler={updateProfilePic}
              userIcon={userIcon}
            />
            {Object.keys(inputData).map((key, i) => (
              <div key={key} className={`relative z-${i}`}>
                <InputNewMemberRow
                  value={inputData[key as IInputUserKey].value}
                  onChangeHandler={(key, value) => {
                    const newData = onChangeHandler(inputData, key, value)
                    setInputDate(newData)
                  }}
                  placeholder={inputData[key as IInputUserKey].placeholder}
                  inputName={inputData[key as IInputUserKey].name}
                  name={inputData[key as IInputUserKey].label}
                  isError={inputData[key as IInputUserKey].hasError}
                  icon={inputUserIcons[inputData[key as IInputUserKey].name as IInputUserKey]}
                  isRequired={inputData[key as IInputUserKey].required}
                  type={inputData[key as IInputUserKey].type}
                  selectData={inputData[key as IInputUserKey].selectData || []}
                />
              </div>
            ))}
          </div>

          <InputCheckMemberInfo
            isChecked={isConfirmed}
            onClick={() => setIsConfirmed(!isConfirmed)}
            text={
              'By ticking this box I confirm that I have been given permission to create a Pegasus\n' +
              'account on behalf of this person, and I am authorized to agree to Pegasus’ terms and\n' +
              'conditions on their behalf, as well as register them for events.'
            }
          />

          <div className={'flex mt-4 gap-4 bg-[#F6F7FB] py-[12px] px-[20px] rounded-[12px]'}>
            <div className={'mt-1'}>
              <IconError />
            </div>

            <p className={'flex-1 text-[#122B46] text-[14px] m-0'}>
              The email address listed above will be notified of this account creation and provided
              with login details to join Pegasus and complete their profile.
            </p>
          </div>
        </div>
      </MainModal>
    </>
  )
}
